import { useEffect, useState, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { useFilters } from "@/context/FiltersContext";
import { api, fmtINR, fmtPct, firstOfMonthISO, todayISO } from "@/lib/api";
import {
  TrendingUp, Handshake, Users, Calendar, Trophy, AlertTriangle,
  ArrowUpRight, Sparkles,
} from "lucide-react";
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip,
  PieChart, Pie, Cell, BarChart, Bar,
} from "recharts";

const RAG = { on_target: "#059669", warning: "#F59E0B", below_target: "#DC2626" };
const COLORS = ["#C9A24B", "#059669", "#3B82F6", "#8B5CF6", "#DC2626", "#F59E0B", "#EC4899", "#14B8A6", "#F97316", "#64748B"];

function KPI({ icon: Icon, label, value, sub, accent = "gold", testid }) {
  const ring = accent === "gold" ? "border-[#C9A24B]/25" : accent === "green" ? "border-emerald-500/25" : "border-slate-700/60";
  const iconBg = accent === "gold" ? "bg-[#C9A24B]/15 text-[#C9A24B]" : accent === "green" ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-700/50 text-slate-200";
  return (
    <div data-testid={testid} className={`bg-[#1E293B] rounded-xl p-5 border ${ring} card-hover`}>
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconBg}`}>
          <Icon size={18} />
        </div>
        <ArrowUpRight size={16} className="text-slate-500" />
      </div>
      <div className="mt-4 text-[11px] uppercase tracking-[0.15em] text-slate-400 font-semibold">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-white" style={{ fontFamily: "Outfit" }}>{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { selectedExecId, dateFrom, setDateFrom, dateTo, setDateTo, executives } = useFilters();
  const [summary, setSummary] = useState(null);
  const [insights, setInsights] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);

  const params = useMemo(() => {
    const p = { date_from: dateFrom, date_to: dateTo };
    if (user?.role !== "executive" && selectedExecId !== "all") p.executive_id = selectedExecId;
    return p;
  }, [user, selectedExecId, dateFrom, dateTo]);

  useEffect(() => {
    api.get("/dashboard/summary", { params }).then((r) => setSummary(r.data));
    api.get("/ai/insights").then((r) => setInsights(r.data.insights || []));
    api.get("/dashboard/leaderboard").then((r) => setLeaderboard(r.data));
  }, [params]);

  if (!summary) return <div className="text-slate-400">Loading dashboard…</div>;

  const t = summary.totals;
  const typeData = Object.entries(summary.type_breakdown).map(([k, v]) => ({ name: k, value: v }));
  const funnelData = [
    { stage: "Contacted", value: summary.stage_breakdown["Contacted"] },
    { stage: "In Discussion", value: summary.stage_breakdown["In Discussion"] },
    { stage: "Agreement Signed", value: summary.stage_breakdown["Agreement Signed"] },
    { stage: "Active/Referring", value: summary.stage_breakdown["Active/Referring"] },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header + filter */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#C9A24B]">Executive Overview</div>
          <h2 className="text-3xl font-light text-white mt-1" style={{ fontFamily: "Outfit" }}>
            {user?.role === "executive" ? `Hello, ${user.name.split(" ")[0]}` : "Team Performance"}
          </h2>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <div className="flex items-center bg-slate-800/60 border border-slate-700 rounded-lg overflow-hidden">
            <input data-testid="dashboard-date-from" type="date" value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="bg-transparent text-white px-3 py-1.5 text-sm outline-none" />
            <span className="text-slate-500 px-2">→</span>
            <input data-testid="dashboard-date-to" type="date" value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="bg-transparent text-white px-3 py-1.5 text-sm outline-none" />
          </div>
          <button
            data-testid="dashboard-reset"
            onClick={() => { setDateFrom(firstOfMonthISO()); setDateTo(todayISO()); }}
            className="text-xs text-slate-300 hover:text-white bg-slate-800/70 hover:bg-slate-700 rounded-md px-3 py-1.5"
          >Reset</button>
        </div>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <KPI icon={TrendingUp} label="Team Revenue" value={fmtINR(t.revenue)}
             sub={`${t.log_count} logs in range`} accent="gold" testid="kpi-revenue" />
        <KPI icon={Handshake} label="Active Tie-Ups" value={`${summary.active_tieups}`}
             sub={`${summary.total_tieups} total in pipeline`} accent="green" testid="kpi-active-tieups" />
        <KPI icon={Users} label="Referral Conversion" value={fmtPct(t.referral_conv_pct)}
             sub={`${t.referrals_converted} / ${t.referrals_generated}`} testid="kpi-conv" />
        <KPI icon={Calendar} label="Events Organized" value={`${t.events_organized}`}
             sub={`${t.reports_submitted} reports submitted`} accent="green" testid="kpi-events" />
        <KPI icon={Trophy} label="Avg Discipline Score" value={`${t.avg_discipline}/10`}
             sub={t.avg_discipline >= 8 ? "Gold tier" : t.avg_discipline >= 6 ? "Silver tier" : "Below Silver"}
             accent="gold" testid="kpi-discipline" />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Revenue trend */}
        <div className="lg:col-span-8 bg-[#1E293B] rounded-xl border border-slate-800 p-6" data-testid="chart-revenue-trend">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Revenue Trend</div>
              <div className="text-white text-lg font-medium mt-0.5" style={{ fontFamily: "Outfit" }}>
                Daily Team Revenue
              </div>
            </div>
            <div className="text-emerald-400 text-sm font-semibold">{fmtINR(t.revenue)} total</div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={summary.revenue_trend}>
              <XAxis dataKey="date" tick={{ fill: "#94A3B8", fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
              <YAxis tick={{ fill: "#94A3B8", fontSize: 11 }} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
              <Tooltip contentStyle={{ backgroundColor: "#0F172A", border: "1px solid #334155", borderRadius: 8, color: "#fff" }}
                       formatter={(v) => fmtINR(v)} />
              <Line type="monotone" dataKey="revenue" stroke="#C9A24B" strokeWidth={2.5} dot={{ fill: "#C9A24B", r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Tie-up type breakdown */}
        <div className="lg:col-span-4 bg-[#1E293B] rounded-xl border border-slate-800 p-6" data-testid="chart-tieup-types">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Tie-Up Mix</div>
          <div className="text-white text-lg font-medium mt-0.5 mb-3" style={{ fontFamily: "Outfit" }}>
            By Partner Type
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={typeData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2}>
                {typeData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ backgroundColor: "#0F172A", border: "1px solid #334155", borderRadius: 8, color: "#fff" }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 gap-1 mt-2 text-[11px]">
            {typeData.slice(0, 6).map((d, i) => (
              <div key={d.name} className="flex items-center gap-1.5 text-slate-300">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                <span className="truncate">{d.name}</span>
                <span className="ml-auto text-slate-400">{d.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Funnel + Insights + Leaderboard */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 bg-[#1E293B] rounded-xl border border-slate-800 p-6" data-testid="chart-funnel">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Referral Pipeline</div>
          <div className="text-white text-lg font-medium mt-0.5 mb-3" style={{ fontFamily: "Outfit" }}>
            Tie-Up Funnel
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={funnelData} layout="vertical">
              <XAxis type="number" tick={{ fill: "#94A3B8", fontSize: 11 }} />
              <YAxis type="category" dataKey="stage" width={130} tick={{ fill: "#E2E8F0", fontSize: 12 }} />
              <Tooltip contentStyle={{ backgroundColor: "#0F172A", border: "1px solid #334155", borderRadius: 8, color: "#fff" }} />
              <Bar dataKey="value" fill="#C9A24B" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="lg:col-span-4 bg-[#1E293B] rounded-xl border border-slate-800 p-6" data-testid="panel-insights">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={16} className="text-[#C9A24B]" />
            <div className="text-white font-medium" style={{ fontFamily: "Outfit" }}>Auto Insights</div>
          </div>
          <div className="space-y-2">
            {insights.map((ins, i) => (
              <div key={i} className="text-sm text-slate-200 bg-slate-800/50 rounded-lg px-3 py-2 border border-slate-700/40">
                {ins}
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-3 bg-[#1E293B] rounded-xl border border-slate-800 p-6" data-testid="panel-leaderboard">
          <div className="flex items-center gap-2 mb-3">
            <Trophy size={16} className="text-[#C9A24B]" />
            <div className="text-white font-medium" style={{ fontFamily: "Outfit" }}>Leaderboard</div>
          </div>
          <div className="space-y-2">
            {leaderboard.map((row, i) => (
              <div key={row.executive_id}
                   className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-800/40 border border-slate-700/40">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                  i === 0 ? "bg-[#C9A24B] text-[#0F172A]" : "bg-slate-700 text-white"
                }`}>#{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm font-medium truncate">{row.executive_name}</div>
                  <div className="text-xs text-slate-400">{fmtINR(row.revenue)}</div>
                </div>
                <div className="text-xs text-emerald-400 font-semibold">{row.avg_discipline}/10</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Overdue banner */}
      {summary.overdue_followups > 0 && (
        <div className="bg-[#DC2626]/10 border border-[#DC2626]/30 rounded-xl p-4 flex items-start gap-3" data-testid="alert-overdue">
          <AlertTriangle size={18} className="text-[#DC2626] mt-0.5" />
          <div>
            <div className="text-white font-medium">
              {summary.overdue_followups} tie-up follow-up{summary.overdue_followups > 1 ? "s" : ""} overdue
            </div>
            <div className="text-sm text-slate-300 mt-0.5">
              Open the Partner Tie-Ups module to review and reschedule.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
